import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { describeDevice, sessionStore, sessionsStore } from '@hds/identity-client-sdk';

// The Active sessions section of Sign-in & security, the same on a phone and a
// desktop: the person's sessions, one row per device, a way to sign out each of
// their other devices, and a way to sign out everywhere. The page owns the
// confirm it shows them in — a sheet on a phone, a drawer on a desktop — and
// hands its state in here.
//
// A failure answers inside the open confirm, and confirming again retries —
// never a toast (identity effort ruling 2026-09-21). Signing out everywhere
// leaves the app when it works, so its confirm stays busy until the page goes.

const SIGN_IN_METHODS = Object.freeze({ google: 'Google', microsoft: 'Microsoft' });

function iconFor(system) {
    if (system === 'iPhone' || system === 'Android') {
        return 'pi-mobile';
    }
    if (system === 'iPad') {
        return 'pi-tablet';
    }
    return 'pi-desktop';
}

export function useActiveSessions({ confirm, busy }) {
    const { t, locale } = useI18n();
    const sessions = sessionsStore();
    const session = sessionStore();

    function when(iso) {
        return new Date(iso).toLocaleString(locale.value, { dateStyle: 'medium', timeStyle: 'short' });
    }

    function methodName(signInMethod) {
        if (signInMethod === 'local') {
            return t('authentication.linking.providerLocal');
        }
        return SIGN_IN_METHODS[signInMethod] ?? signInMethod;
    }

    // What a row says: the app, the browser on its system, and the details
    // known about it. A detail nobody recorded is left out, not shown blank.
    const rows = computed(() => sessions.sessions.map((row) => {
        const { browser, system } = describeDevice(row.userAgent);
        const details = [];
        if (row.signInMethod) {
            details.push(t('authentication.sessions.signedInWith', { method: methodName(row.signInMethod) }));
        }
        if (row.ipAddress) {
            details.push(t('authentication.sessions.ipAddress', { address: row.ipAddress }));
        }
        details.push(t('authentication.sessions.signedInAt', { date: when(row.signedInAt) }));
        details.push(t('authentication.sessions.lastActive', { date: when(row.lastActiveAt) }));
        return {
            id: row.id,
            isThisDevice: row.isThisDevice,
            icon: iconFor(system),
            appName: t(`authentication.sessions.apps.${row.appId}`, row.appId),
            device: t('authentication.sessions.browserOnSystem', {
                browser: browser ?? t('authentication.sessions.unknownBrowser'),
                system: system ?? t('authentication.sessions.unknownSystem')
            }),
            details
        };
    }));

    async function attempt(action, { leavesTheApp = false } = {}) {
        busy.value = true;
        confirm.value.error = '';
        try {
            await action();
            if (!leavesTheApp) {
                confirm.value.open = false;
                busy.value = false;
            }
        } catch {
            confirm.value.error = t('authentication.sessions.failed');
            busy.value = false;
        }
    }

    function askSignOutDevice(row) {
        confirm.value = {
            open: true,
            title: t('authentication.sessions.signOutTitle'),
            message: t('authentication.sessions.signOutMessage', { device: `${row.appName}, ${row.device}` }),
            confirmLabel: t('authentication.sessions.signOutConfirm'),
            cancelLabel: t('authentication.sessions.cancel'),
            destructive: true,
            slide: false,
            error: '',
            action: () => attempt(() => sessions.signOutDevice(row.id))
        };
    }

    function askSignOutEverywhere() {
        confirm.value = {
            open: true,
            title: t('authentication.sessions.everywhereTitle'),
            message: t('authentication.sessions.everywhereMessage'),
            confirmLabel: t('authentication.sessions.everywhereConfirm'),
            cancelLabel: t('authentication.sessions.cancel'),
            destructive: true,
            slide: false,
            error: '',
            action: () => attempt(() => session.logoutEverywhere(), { leavesTheApp: true })
        };
    }

    onMounted(() => sessions.refresh());

    return {
        rows,
        loadFailed: computed(() => sessions.error !== null),
        askSignOutDevice,
        askSignOutEverywhere
    };
}
